import { defaultLocale, TCurrency } from '@/config/locales';
import { IFavorite } from '@/redux/modules/wishlists';
import { calcDiscount } from '@/redux/selectors/listing/bill';
import { PriceType } from '@/redux/selectors/listing/page';
import { parseFuelTypeOfVehicle } from '@/redux/selectors/listing/rentalDetails';
import { IBooking } from '@/services/types/booking/details';
import { ITag, TAutoRentalData } from '@/services/types/search/rentals/id';

import { getOptimisticFavoriteCount } from './getOptimisticFavoriteCount';
import { getIntl } from './i18n';
import { IPrice, ISearchResultTile } from './mapSearchResultToTile';

const IMAGES_LIMIT = 7;

type TTAutoRentalDetails = {
  location?: string;
  city?: string;
  ratingNumber?: number;
  ratingQtd?: number;
  year: number;
  make: string;
  model: string;
  seatbelts?: string;
  fuelType?: string;
  tags?: ITag[];
  distance?: number;
  distanceUnit?: string;
  delivery?: boolean;
};

export type TAutoRentalTile = {
  id?: number;
  currency?: TCurrency;
  dealer?: boolean;
  details?: TTAutoRentalDetails;
  instantBook?: boolean;
  wasBookedBefore?: boolean;
  isFavorite?: boolean;
  linkUrl?: string;
  loading?: boolean;
  photoUrl?: string | string[];
  photoAlt?: string;
  title?: string;
  price?: IPrice;
  favoriteCount?: number;
  lat?: number;
  lng?: number;
  wishlistID?: number;
  wishlistName?: string;
  wishlistCreatorID?: number | null;
  photoMetadata?: Array<{
    url: string;
    category?: string;
    tags?: string;
  }>;
  __typename: 'TAutoRentalTile';
};

const mapAutoRentalToPrice = (rental: TAutoRentalData) => {
  const currentPrice =
    rental.active_options?.day_price || rental.price_per_day || rental.active_price?.day || 0;
  const originalPrice = rental.price_per_day || rental.active_price?.day || 0;

  return {
    currency: rental.presentment_currency || defaultLocale.base_currency,
    discount: calcDiscount(originalPrice, currentPrice),
    instantBook: rental.active_options?.instant_book ?? rental.instant_book,
    currentPrice,
    originalPrice,
    vacationPackageNightlyRate: rental.vacation_package_nightly_rate,
    // TODO: Temporary mark all as price per day, remove when we fill the data in the database
    period: PriceType.DAY, // rental.active_options?.use_day_pricing ? PriceType.DAY : PriceType.NIGHT,
    weeklyDiscountPercentage: rental.active_options?.weekly_discount_percentage,
    monthlyDiscountPercentage: rental.active_options?.monthly_discount_percentage,
    estimatedPriceBreakdown: rental.active_options?.estimated_price_breakdown,
  };
};

type TMapAutoRentalToTileParams = {
  rental: TAutoRentalData;
  favorites?: IFavorite[];
  bookings?: IBooking[];
};

const mapAutoRentalToTile = ({
  rental,
  favorites,
  bookings,
}: TMapAutoRentalToTileParams): TAutoRentalTile => {
  const intl = getIntl();

  const favoriteRecord = favorites?.find(favorite => favorite.item_id === rental.id);

  const year = rental.vehicle?.year || rental.vehicle_year;
  const make = rental.vehicle?.make || rental.vehicle_make;
  const model = rental.vehicle?.model || rental.vehicle_model;

  const location = rental.location
    ? `${rental.location?.city}${rental.location?.state && `, ${rental.location?.state}`}`
    : `${rental?.home?.city}${rental?.home?.state && `, ${rental?.home?.state}`}`;

  const images = rental.images?.map(image => image.url).slice(0, IMAGES_LIMIT) || [];
  const primaryImage = rental.primary_image_url || images?.[0];
  const imageMetadata = rental.images
    ? rental.images.map(image => ({
        url: image.url,
        category: image.category?.slug,
        tags: image.tags,
      }))
    : [];

  const seatbelts = rental.seatbelts
    ? intl.formatMessage(
        { defaultMessage: 'Seatbelts {seatbelts}', id: 'B/pr1O' },
        { seatbelts: rental.seatbelts },
      )
    : undefined;

  const fuelType = parseFuelTypeOfVehicle(rental.features.fuel_type).fuel_type?.label;

  return {
    id: rental.id,
    currency: rental.presentment_currency,
    dealer: rental.dealer,
    details: {
      location,
      city: rental.location?.city || rental?.home?.city || '',
      ratingNumber: rental.score,
      ratingQtd: rental.reviews_num ?? rental.score_count,
      year,
      make,
      model,
      seatbelts,
      fuelType,
      tags: rental.tags,
      distance: rental.distance,
      distanceUnit: rental.locale?.distance_unit,
      delivery: rental.delivery,
    },
    instantBook: rental.active_options?.instant_book ?? rental.instant_book,
    wasBookedBefore: bookings?.some(booking => booking.rental_id === rental.id),
    isFavorite: !!favoriteRecord,
    linkUrl: `/auto${rental.slug}`,
    photoUrl: images.length > 0 ? images : primaryImage,
    photoMetadata: imageMetadata.length > 1 ? imageMetadata.slice(0, IMAGES_LIMIT) : imageMetadata,
    photoAlt: `${year} ${make} ${model} rental in ${location}`,
    title: rental.name,
    price: mapAutoRentalToPrice(rental),
    favoriteCount: getOptimisticFavoriteCount(rental, !!favoriteRecord),
    lat: rental.location?.lat,
    lng: rental.location?.lng,
    ...(favoriteRecord && {
      wishlistID: favoriteRecord.wishlist_id,
      wishlistName: favoriteRecord.wishlist_name,
      wishlistCreatorID: favoriteRecord.wishlist_creator_id,
    }),
    __typename: 'TAutoRentalTile',
  };
};

type TMapAutoRentalsToTilesParams = {
  rentals: TAutoRentalData[];
  favorites?: IFavorite[];
  bookings?: IBooking[];
};

export const mapAutoRentalsToTiles = ({
  rentals,
  favorites,
  bookings,
}: TMapAutoRentalsToTilesParams): TAutoRentalTile[] =>
  rentals?.map(rental =>
    mapAutoRentalToTile({
      rental,
      favorites,
      bookings,
    }),
  ) || [];

export const isAutoRentalTile = (
  tile: ISearchResultTile | TAutoRentalTile,
): tile is TAutoRentalTile => {
  return '__typename' in tile && tile.__typename === 'TAutoRentalTile';
};
